import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField'
import type { DateRange } from '@mui/x-date-pickers-pro'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import Close from '@mui/icons-material/Close'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Search from '@mui/icons-material/Search'
import Tune from '@mui/icons-material/Tune'
import dayjs, { Dayjs } from 'dayjs'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import type { EventTypeName } from 'src/v3/core/types'
import {
  accreditationFilterChanged,
  categoryFilterChanged,
  dateLowerFilterChanged,
  dateUpperFilterChanged,
  eventTypeFilterChanged,
  filtersReset,
  instructorFilterChanged,
  linkedServiceFilterChanged,
  searchQueryChanged,
  selectAccreditationFilter,
  selectCategoryFilter,
  selectDateLowerFilter,
  selectDateUpperFilter,
  selectEventTypeFilter,
  selectInstructorFilter,
  selectInstructorOptions,
  selectLinkedServiceFilter,
  selectLinkedServices,
  selectSearchQuery,
} from '../events-v2.slice'
import { useDebouncedValue } from '../hooks/use-debounced-value'

let EVENT_TYPE_OPTIONS: { value: EventTypeName; label: string }[] = [
  { value: 'virtual', label: 'Virtual' },
  { value: 'inPerson', label: 'In Person' },
  { value: 'hybrid', label: 'Hybrid' },
]

let CATEGORY_OPTIONS = [
  {
    value: 'airway|Respiratory|Ventilation',
    label: 'Airway / Respiratory / Ventilation',
  },
  { value: 'medical', label: 'Medical' },
  { value: 'trauma', label: 'Trauma' },
  { value: 'operations', label: 'Operations' },
  { value: 'criticalCare', label: 'Critical Care' },
  { value: 'cardiovascular', label: 'Cardiovascular' },
  { value: 'pediatrics', label: 'Pediatrics' },
  { value: 'other', label: 'Other' },
]

let ACCREDITATION_OPTIONS = [
  { value: 'ancc', label: 'ANCC' },
  { value: 'capce', label: 'CAPCE' },
  { value: 'newJersey', label: 'New Jersey' },
  { value: 'newYork', label: 'New York' },
  { value: 'virginia', label: 'Virginia' },
  { value: 'ceBroker', label: 'CE Broker' },
  { value: 'accredited', label: 'Accredited' },
  { value: 'notAccredited', label: 'Not Accredited' },
]

type Props = {
  filteredCount: number
}

let EventsFilterPanel = ({ filteredCount }: Props) => {
  let dispatch = useAppDispatch()
  let [expanded, setExpanded] = useState(false)

  let searchQuery = useAppSelector(selectSearchQuery)
  let linkedServiceFilter = useAppSelector(selectLinkedServiceFilter)
  let eventTypeFilter = useAppSelector(selectEventTypeFilter)
  let dateLowerFilter = useAppSelector(selectDateLowerFilter)
  let dateUpperFilter = useAppSelector(selectDateUpperFilter)
  let categoryFilter = useAppSelector(selectCategoryFilter)
  let accreditationFilter = useAppSelector(selectAccreditationFilter)
  let instructorFilter = useAppSelector(selectInstructorFilter)
  let instructorOptions = useAppSelector(selectInstructorOptions)
  let linkedServices = useAppSelector(selectLinkedServices)

  let [inputSearch, setInputSearch] = useState(searchQuery)
  let debouncedSearch = useDebouncedValue(inputSearch)

  useEffect(() => {
    dispatch(searchQueryChanged(debouncedSearch))
  }, [debouncedSearch, dispatch])

  let hasDateFilter = dateLowerFilter != null || dateUpperFilter != null

  let chipCount =
    (searchQuery ? 1 : 0) +
    (linkedServiceFilter != null ? 1 : 0) +
    (eventTypeFilter !== 'all' ? 1 : 0) +
    (hasDateFilter ? 1 : 0) +
    (categoryFilter != null ? 1 : 0) +
    (accreditationFilter != null ? 1 : 0) +
    (instructorFilter != null ? 1 : 0)

  let hasAnyActiveFilter = chipCount > 0

  let handleReset = () => {
    setInputSearch('')
    dispatch(filtersReset())
  }

  let handleDateRangeChange = ([from, to]: DateRange<Dayjs>) => {
    dispatch(dateLowerFilterChanged(from ? from.toISOString() : null))
    dispatch(dateUpperFilterChanged(to ? to.toISOString() : null))
  }

  let linkedServiceLabel = linkedServiceFilter
    ? linkedServiceFilter === 'FOAMfrat'
      ? 'FOAMfrat'
      : (linkedServices.find((ls) => ls.id === linkedServiceFilter)?.name ??
        linkedServiceFilter)
    : null

  let eventTypeLabel =
    eventTypeFilter !== 'all'
      ? (EVENT_TYPE_OPTIONS.find((o) => o.value === eventTypeFilter)?.label ??
        eventTypeFilter)
      : null

  let dateRangeLabel = (() => {
    if (!hasDateFilter) return null
    let parts = [
      dateLowerFilter ? dayjs(dateLowerFilter).format('MMM D') : null,
      dateUpperFilter ? dayjs(dateUpperFilter).format('MMM D') : null,
    ].filter((p): p is string => p != null)
    return `Date: ${parts.join(' – ')}`
  })()

  let categoryLabel = categoryFilter
    ? (CATEGORY_OPTIONS.find((o) => o.value === categoryFilter)?.label ??
      categoryFilter)
    : null

  let accreditationLabel = accreditationFilter
    ? (ACCREDITATION_OPTIONS.find((o) => o.value === accreditationFilter)
        ?.label ?? accreditationFilter)
    : null

  let instructorLabel = instructorFilter
    ? (instructorOptions.find((i) => i.id === instructorFilter)?.name ??
      instructorFilter)
    : null

  return (
    <Box className="grid pt-6 mb-3">
      <Box
        className="grid gap-4 mb-4"
        sx={{
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            lg: '1fr 1fr 1fr',
          },
        }}
      >
        <TextField
          value={inputSearch}
          fullWidth
          onChange={(e) => setInputSearch(e.target.value)}
          placeholder="Search events"
          size="medium"
          className="grow"
          slotProps={{
            htmlInput: { 'aria-label': 'Search events' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ bgcolor: 'background.paperDark' }}
        />
        <Button
          variant="outlined"
          size="medium"
          className="grow"
          sx={{ height: '56px', maxWidth: { xs: '100%', sm: '130px' } }}
          startIcon={<Tune />}
          endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          aria-expanded={expanded}
          aria-controls="events-secondary-filters"
          onClick={() => setExpanded((v) => !v)}
        >
          Filters
        </Button>
        {hasAnyActiveFilter && (
          <Box
            className="flex items-center"
            sx={{
              justifyContent: { xs: 'center', lg: 'right' },
              gridColumn: { sm: 'span 2', lg: '3' },
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              className="items-center whitespace-nowrap"
            >
              {chipCount} filter{chipCount !== 1 ? 's' : ''} applied - showing{' '}
              {filteredCount} session{filteredCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Box>

      <Collapse in={expanded}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box
            id="events-secondary-filters"
            className="grid gap-4 mb-4"
            sx={{
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                lg: '1fr 1fr 1fr',
              },
            }}
          >
            <FormControl
              size="medium"
              fullWidth
              sx={{ bgcolor: 'background.paperDark' }}
            >
              <InputLabel id="filter-linked-service-label" shrink>
                All linked services
              </InputLabel>
              <Select
                labelId="filter-linked-service-label"
                label="All linked services"
                value={linkedServiceFilter ?? ''}
                displayEmpty
                onChange={(e) =>
                  dispatch(linkedServiceFilterChanged(e.target.value || null))
                }
              >
                <MenuItem value="">All linked services</MenuItem>
                <MenuItem value="FOAMfrat">FOAMfrat</MenuItem>
                {linkedServices.map((ls) => (
                  <MenuItem key={ls.id} value={ls.id}>
                    {ls.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DateRangePicker
              value={[
                dateLowerFilter ? dayjs(dateLowerFilter) : null,
                dateUpperFilter ? dayjs(dateUpperFilter) : null,
              ]}
              onChange={handleDateRangeChange}
              label="Date range"
              slots={{ field: SingleInputDateRangeField }}
              slotProps={{
                field: {
                  size: 'medium',
                  error:
                    dateLowerFilter != null && dateUpperFilter != null
                      ? undefined
                      : false,
                  sx: {
                    '& .MuiPickersInputBase-root': {
                      backgroundColor: 'background.paperDark',
                    },
                  },
                } as any,
              }}
            />

            <FormControl
              size="medium"
              fullWidth
              sx={{ bgcolor: 'background.paperDark' }}
            >
              <InputLabel id="filter-event-type-label" shrink>
                All types
              </InputLabel>
              <Select
                labelId="filter-event-type-label"
                label="All types"
                value={eventTypeFilter}
                displayEmpty
                onChange={(e) =>
                  dispatch(
                    eventTypeFilterChanged(
                      e.target.value as EventTypeName | 'all',
                    ),
                  )
                }
              >
                <MenuItem value="all">All types</MenuItem>
                {EVENT_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="medium"
              fullWidth
              sx={{ bgcolor: 'background.paperDark' }}
            >
              <InputLabel id="filter-category-label" shrink>
                All categories
              </InputLabel>
              <Select
                labelId="filter-category-label"
                label="All categories"
                value={categoryFilter ?? ''}
                displayEmpty
                onChange={(e) =>
                  dispatch(categoryFilterChanged(e.target.value || null))
                }
              >
                <MenuItem value="">All categories</MenuItem>
                {CATEGORY_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="medium"
              fullWidth
              sx={{ bgcolor: 'background.paperDark' }}
            >
              <InputLabel id="filter-accreditation-label" shrink>
                All accreditation
              </InputLabel>
              <Select
                labelId="filter-accreditation-label"
                label="All accreditations"
                value={accreditationFilter ?? ''}
                displayEmpty
                onChange={(e) =>
                  dispatch(accreditationFilterChanged(e.target.value || null))
                }
              >
                <MenuItem value="">All accreditation</MenuItem>
                {ACCREDITATION_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="medium"
              fullWidth
              sx={{ bgcolor: 'background.paperDark' }}
            >
              <InputLabel id="filter-instructor-label" shrink>
                All instructors
              </InputLabel>
              <Select
                labelId="filter-instructor-label"
                label="All instructors"
                value={instructorFilter ?? ''}
                displayEmpty
                onChange={(e) =>
                  dispatch(instructorFilterChanged(e.target.value || null))
                }
              >
                <MenuItem value="">All instructors</MenuItem>
                {instructorOptions.map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </LocalizationProvider>
      </Collapse>

      {hasAnyActiveFilter && (
        <Box
          className="grid gap-4"
          sx={{
            gridTemplateColumns: {
              xs: '1fr',
              lg: '1fr 1fr 1fr',
            },
          }}
        >
          <Box
            className="flex flex-wrap items-center gap-2 mt-4"
            sx={{ gridColumn: { lg: 'span 2' } }}
          >
            {searchQuery && (
              <Chip
                size="medium"
                label={`Search: "${searchQuery}"`}
                aria-label={`Remove search filter: ${searchQuery}`}
                onDelete={() => {
                  setInputSearch('')
                  dispatch(searchQueryChanged(''))
                }}
              />
            )}
            {linkedServiceLabel && (
              <Chip
                size="medium"
                label={`Service: ${linkedServiceLabel}`}
                aria-label={`Remove linked service filter: ${linkedServiceLabel}`}
                onDelete={() => dispatch(linkedServiceFilterChanged(null))}
              />
            )}
            {eventTypeLabel && (
              <Chip
                size="medium"
                label={`Type: ${eventTypeLabel}`}
                aria-label={`Remove event type filter: ${eventTypeLabel}`}
                onDelete={() => dispatch(eventTypeFilterChanged('all'))}
              />
            )}
            {dateRangeLabel && (
              <Chip
                size="medium"
                label={dateRangeLabel}
                aria-label="Remove date filter"
                onDelete={() => {
                  dispatch(dateLowerFilterChanged(null))
                  dispatch(dateUpperFilterChanged(null))
                }}
              />
            )}
            {categoryLabel && (
              <Chip
                size="medium"
                label={`Category: ${categoryLabel}`}
                aria-label={`Remove category filter: ${categoryLabel}`}
                onDelete={() => dispatch(categoryFilterChanged(null))}
              />
            )}
            {accreditationLabel && (
              <Chip
                size="medium"
                label={`Accreditation: ${accreditationLabel}`}
                aria-label={`Remove accreditation filter: ${accreditationLabel}`}
                onDelete={() => dispatch(accreditationFilterChanged(null))}
              />
            )}
            {instructorLabel && (
              <Chip
                size="medium"
                label={`Instructor: ${instructorLabel}`}
                aria-label={`Remove instructor filter: ${instructorLabel}`}
                onDelete={() => dispatch(instructorFilterChanged(null))}
              />
            )}
          </Box>
          <Box
            className="flex items-start"
            sx={{ justifyContent: { xs: 'center', lg: 'right' } }}
          >
            <Button
              size="medium"
              color="secondary"
              startIcon={<Close />}
              onClick={handleReset}
            >
              Reset filters
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default EventsFilterPanel
